/* eslint-disable import/no-duplicates */
import { FormControl, IconButton, styled } from "@mui/material";
import {
  Controller,
  FieldPath,
  FieldValues,
  useFormContext,
} from "react-hook-form";
import { useState } from "react";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import {
  DatePicker as MuiDatePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { enGB } from "date-fns/locale";
import { addDays } from "date-fns";
import { COLORS } from "../../../constants/colors";

const DatePickerIcon = styled("i")`
  width: 24px;
  height: 24px;
`;

const StyledContainer = styled("div")`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

const StyledMuiDataPicker = styled(MuiDatePicker)`
  align-items: center;

  .MuiInputAdornment-root {
    margin-left: 0;
  }

  .MuiInputBase-root {
    padding-right: 8px;
  }

  .MuiInputBase-input {
    max-width: 110px;
    outline: 0;
    padding-left: 0;
    font-size: 20px;
    font-weight: 500;
    color: ${COLORS.TEXT_MEDIUM_EMPHASIS};
  }

  .Mui-error .MuiInputBase-input {
    color: ${COLORS.PRIMARY};
  }

  fieldset {
    border-width: 0;
  }
  .Mui-focused fieldset {
    border: 0 !important;
  }
`;

interface DatePickerProps<FormValues extends FieldValues = FieldValues> {
  name: FieldPath<FormValues>;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
}

export const DatePicker = <FormValues extends FieldValues = FieldValues>({
  name,
  minDate,
  maxDate,
  disabled,
}: DatePickerProps<FormValues>) => {
  const { control, setValue } = useFormContext<FormValues>();
  const [opened, setOpened] = useState(false);
  const currentLangLocale = enGB;

  const datePickerOptions = {
    clearable: false,
    inputFormat: "dd/MM/yyyy",
    KeyboardButtonProps: {
      "aria-label": "change date",
    },
    keyboardIcon: <DatePickerIcon className="udo-icon-datepicker" />,
    nextIconButton: <DatePickerIcon className="udo-icon-datepicker" />,
    cancelLabel: "Cancel",
    clearLabel: "Clear",
    okLabel: "Ok",
  };

  const handleButtonClick = (value: Date | null, days: number) => {
    if (value) {
      setValue(name, addDays(value, days));
    }
  };

  const checkPreviousButtonDisabled = (value: Date | null): boolean => {
    if (!value) return true;
    if (!minDate) return false;

    return addDays(value, -1) > minDate;
  };

  const checkNextButtonDisabled = (value: Date | null): boolean => {
    if (!value) return true;
    if (!maxDate) return false;

    return addDays(value, 1) > maxDate;
  };

  const handleChange =
    (onChange: (value: Date | null) => void) => (value: Date | null) => {
      if (!value) {
        onChange(value);
        return;
      }

      if (minDate && value < minDate) {
        onChange(minDate);
      } else if (maxDate && value > maxDate) {
        onChange(maxDate);
      } else {
        onChange(value);
      }
    };

  return (
    <FormControl
      variant="standard"
      fullWidth
      sx={{ width: "100%", maxWidth: "250px", flexDirection: "row" }}
    >
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={currentLangLocale}
      >
        <Controller
          name={name}
          control={control}
          render={({ field: { onChange, value: rawValue } }) => {
            const candidate: unknown = rawValue;
            const value = candidate instanceof Date ? candidate : null;

            return (
              <StyledContainer>
                <IconButton
                  onClick={() => handleButtonClick(value, -1)}
                  disabled={checkPreviousButtonDisabled(value)}
                  size="medium"
                  sx={{ marginRight: "4px" }}
                >
                  <NavigateBeforeIcon fontSize="inherit" />
                </IconButton>
                <StyledMuiDataPicker
                  {...datePickerOptions}
                  value={value}
                  onChange={handleChange(onChange)}
                  open={opened}
                  onOpen={() => setOpened(true)}
                  onClose={() => setOpened(false)}
                  minDate={minDate}
                  maxDate={maxDate}
                  disabled={disabled}
                />
                <IconButton
                  onClick={() => handleButtonClick(value, 1)}
                  disabled={checkNextButtonDisabled(value)}
                  size="medium"
                >
                  <NavigateNextIcon fontSize="inherit" />
                </IconButton>
              </StyledContainer>
            );
          }}
        />
      </LocalizationProvider>
    </FormControl>
  );
};
